/*
 * Stock urgency message ("Limited stock. Act fast!" / "Sold out")
 * ------------------------------------------------------------------
 * This custom element backs the "Almost sold out pulse" liquid block
 * (templates/product.json block liquid_wKFLCK, and the identical
 * duplicate block used by the homepage's Featured Product Tabs section
 * in templates/index.json, block liquid_tcJiP3).
 *
 * WHY THIS IS A CUSTOM ELEMENT INSTEAD OF LIQUID CONDITIONALS:
 * Shopify's "liquid" custom-code block setting type always evaluates
 * using the page's ambient global `product`/`request` context -- it has
 * no access to any locally-scoped `product` variable explicitly
 * forwarded through a render chain (see the identical, already-fixed
 * issue documented for the `price_per_item` and `judgeme_badge` native
 * blocks in snippets/product-info.liquid). That breaks correctness on
 * any template that renders more than one product per page, such as
 * the homepage's Featured Product Tabs section: there is no single
 * "current product" for the ambient global there, so any Liquid
 * conditional inside a raw "liquid" block referencing
 * `product.selected_or_first_available_variant.available` would always
 * evaluate against the wrong (or no) product.
 *
 * This element sidesteps the problem entirely by determining
 * availability purely on the client, from the *nearest* product's own
 * Add to Cart button -- which is always correctly scoped, since
 * snippets/buy-buttons.liquid renders one <form is="product-form">
 * per product instance and already computes `button_disabled`/`disabled`
 * from that instance's own local `product` variable (covering plain
 * variant availability as well as this store's bundle/pack logic).
 *
 * It reacts to live changes via a MutationObserver on that button's
 * `disabled`/`aria-disabled` attributes, so the message updates
 * immediately on size/color changes without needing a page reload --
 * on both the PDP (sections/main-product.liquid) and the Featured
 * Product Tabs (snippets/alden-product-panel.liquid), both of which
 * wrap this markup in <product-rerender>.
 *
 * Registered once, globally: because this is a real custom element
 * (not an inline <script> depending on re-execution), it upgrades
 * automatically on every future insertion into the DOM -- including
 * when <product-rerender allow-partial-rerender> replaces its own
 * subtree wholesale on a variant change. No per-render script
 * re-execution is required.
 */
(() => {
  'use strict';

  if (customElements.get('stock-urgency-message')) return;

  class StockUrgencyMessage extends HTMLElement {
    connectedCallback() {
      this._tries = 0;
      this._attach();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    _attach() {
      const root = this.closest('product-rerender') || this.closest('.shopify-section') || document;
      const form = root.querySelector('form[is="product-form"]');
      const button = form ? form.querySelector('button[type="submit"]') : null;

      if (!button) {
        if (this._tries++ < 60) {
          requestAnimationFrame(() => this._attach());
        }
        return;
      }

      if (this._observer) this._observer.disconnect();
      this._button = button;
      this._sync();

      this._observer = new MutationObserver(() => this._sync());
      this._observer.observe(button, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }

    _sync() {
      const button = this._button;
      const unavailable = Boolean(button && (button.disabled || button.getAttribute('aria-disabled') === 'true'));

      this.classList.toggle('limited-stock-banner--sold-out', unavailable);

      const message = this.querySelector('.limited-stock-banner__message');
      if (!message) return;

      const availableLabel = this.dataset.availableLabel || 'Limited stock. Act fast!';
      const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      message.textContent = unavailable ? soldOutLabel : availableLabel;
    }
  }

  customElements.define('stock-urgency-message', StockUrgencyMessage);
})();